<template>
  <div id="contenedor-default-licitacion" class="bkground">
    <!-- Estado cargando -->
    <template v-if="$apollo.loading">
      <v-row>
        <v-col cols="12" md="4">
          <v-skeleton-loader type="card"></v-skeleton-loader>
        </v-col>
        <v-col cols="12" md="8">
          <v-skeleton-loader type="card"></v-skeleton-loader>
        </v-col>
      </v-row>
    </template>

    <!-- Contenido -->
    <template v-else>
      <v-row>
        <!-- Columna izquierda -->
        <v-col cols="12" md="4">
          <!-- Card Información General -->
          <v-card class="rounded-lg mb-4">
            <v-card-title class="grey darken-3 white--text text-subtitle-1 py-3">
              <v-icon left small color="white">mdi-gavel</v-icon>
              Información General
              <v-spacer></v-spacer>
              <v-chip
                x-small
                :color="estaCancelada ? 'error' : 'success'"
                text-color="white">
                {{ estaCancelada ? 'Cancelada' : 'Activa' }}
              </v-chip>
              <v-tooltip
                top
                v-if="mixValidaScopesActions(3) && !estaCancelada">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on">
                    <v-btn
                      x-small
                      text
                      color="error"
                      :disabled="!puedeCancelarPorRegla"
                      @click="abrirDialogoCancelar">
                      <v-icon left x-small>mdi-cancel</v-icon>
                      Cancelar
                    </v-btn>
                  </span>
                </template>
                <span v-if="!puedeCancelarPorRegla">
                  No se puede cancelar: la licitación ya tiene ganador.
                </span>
                <span v-else>
                  Cancelar licitación
                </span>
              </v-tooltip>
            </v-card-title>
            <v-card-text class="pt-4">
              <v-alert
                v-if="estaCancelada"
                dense
                outlined
                type="error"
                class="mb-4">
                <div class="font-weight-medium">Licitación cancelada</div>
                <div v-if="getLicitacion.motivoCancelacion" class="mt-1">
                  Motivo: {{ getLicitacion.motivoCancelacion }}
                </div>
              </v-alert>

              <v-list dense class="transparent">
                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-pound</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">
                      Número de Licitación
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      {{ getLicitacion.numero }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-format-list-bulleted-type</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">
                      Tipo
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      <v-chip
                        x-small
                        :color="getLicitacion.tipo === 1 ? 'primary' : 'secondary'"
                        text-color="white">
                        {{ getTipoLicitacion }}
                      </v-chip>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-flag</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">
                      Carácter
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      {{ getLicitacion.caracter === 1 ? 'Nacional' : 'Internacional' }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-scale-balance</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">
                      Normatividad
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      <v-chip
                        x-small
                        :color="getLicitacion.normatividad === 1 ? 'info' : 'warning'"
                        text-color="white">
                        {{ getLicitacion.normatividad === 1 ? 'Federal' : 'Estatal' }}
                      </v-chip>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <v-divider class="my-3"></v-divider>

              <div>
                <span class="text-caption grey--text">Objeto de la Licitación</span>
                <p class="text-body-2 mt-1 mb-0">{{ getLicitacion.objeto }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Card Convocatoria -->
          <v-card class="rounded-lg mb-4">
            <v-card-title class="grey darken-3 white--text text-subtitle-1 py-3">
              <v-icon left small color="white">mdi-bullhorn</v-icon>
              Convocatoria
            </v-card-title>
            <v-card-text class="pt-4">
              <v-list dense class="transparent">
                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-numeric</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">
                      Número de Convocatoria
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      {{ getLicitacion.numeroConvocatoria }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-calendar</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">
                      Fecha de Convocatoria
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      {{ formatoFechaHumano(getLicitacion.fechaConvocatoria) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Card Anticipos -->
          <v-card class="rounded-lg">
            <v-card-title class="grey darken-3 white--text text-subtitle-1 py-3">
              <v-icon left small color="white">mdi-cash-multiple</v-icon>
              Anticipos
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row class="text-center">
                <v-col cols="4">
                  <div class="pa-3 rounded-lg" style="background: rgba(0,0,0,0.02);">
                    <div class="text-caption grey--text mb-1">Material</div>
                    <div class="text-h6 font-weight-bold primary--text">
                      {{ getLicitacion.anticipoMaterial }}%
                    </div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="pa-3 rounded-lg" style="background: rgba(0,0,0,0.02);">
                    <div class="text-caption grey--text mb-1">Inicio</div>
                    <div class="text-h6 font-weight-bold">
                      {{ getLicitacion.anticipoInicio }}%
                    </div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="pa-3 rounded-lg success lighten-5">
                    <div class="text-caption grey--text mb-1">Total</div>
                    <div class="text-h6 font-weight-bold success--text">
                      {{ getTotalAnticipo }}%
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Columna derecha -->
        <v-col cols="12" md="8">
          <!-- Card Fechas y Plazos -->
          <v-card class="rounded-lg mb-4">
            <v-card-title class="grey darken-3 white--text text-subtitle-1 py-3">
              <v-icon left small color="white">mdi-calendar-range</v-icon>
              Fechas y Plazos
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12" md="8">
                  <v-timeline dense align-top>
                    <v-timeline-item
                      small
                      color="success"
                      icon="mdi-play">
                      <div>
                        <span class="text-caption grey--text">Fecha de Inicio</span>
                        <div class="font-weight-medium">
                          {{ formatoFechaHumano(getLicitacion.fechaInicio) }}
                        </div>
                      </div>
                    </v-timeline-item>

                    <v-timeline-item
                      small
                      color="warning"
                      icon="mdi-flag-checkered">
                      <div>
                        <span class="text-caption grey--text">Fecha de Término</span>
                        <div class="font-weight-medium">
                          {{ formatoFechaHumano(getLicitacion.fechaTermino) }}
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-center">
                  <div class="text-center">
                    <v-avatar
                      size="80"
                      color="primary lighten-4">
                      <span class="text-h5 primary--text font-weight-bold">
                        {{ getLicitacion.plazo }}
                      </span>
                    </v-avatar>
                    <div class="text-caption grey--text mt-2">
                      días de plazo
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Card Obras Asociadas -->
          <v-card class="rounded-lg">
            <v-card-title class="grey darken-3 white--text text-subtitle-1 py-3">
              <v-icon left small color="white">mdi-office-building</v-icon>
              Obras Asociadas
              <v-chip
                x-small
                color="white"
                text-color="grey darken-3"
                class="ml-2">
                {{ getLicitacion.obras ? getLicitacion.obras.length : 0 }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn
                v-if="mixValidaScopesActions(1)"
                x-small
                color="white"
                text
                @click="showAddObraToLicitacion = true">
                <v-icon left x-small>mdi-plus</v-icon>
                Agregar
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <template v-if="getLicitacion.obras && getLicitacion.obras.length > 0">
                <v-data-table
                  :headers="headers"
                  :items="getLicitacion.obras"
                  disable-pagination
                  disable-filtering
                  hide-default-footer
                  class="elevation-0">
                  <template v-slot:[`item.obra.clave`]="{ item }">
                    <span class="font-weight-medium primary--text">
                      {{ item.obra.clave }}
                    </span>
                  </template>
                  <template v-slot:[`item.obra.tipo`]="{ item }">
                    <v-chip small :color="item.obra.tipo.color">
                      {{ item.obra.tipo.descripcion }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.obra.meta`]="{ item }">
                    <span class="text-body-2">{{ item.obra.meta }}</span>
                  </template>
                  <template v-slot:[`item.acciones`]="{ item }">
                    <v-btn
                      v-if="mixValidaScopesActions(3)"
                      icon
                      small
                      color="error"
                      @click="removerObra(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </template>
              <template v-else>
                <div class="text-center py-10">
                  <v-icon size="60" color="grey lighten-2">
                    mdi-office-building-outline
                  </v-icon>
                  <p class="text-body-1 grey--text mt-4 mb-0">
                    Esta licitación no tiene obras registradas
                  </p>
                  <v-btn
                    v-if="mixValidaScopesActions(1)"
                    small
                    color="primary"
                    class="mt-4"
                    @click="showAddObraToLicitacion = true">
                    <v-icon left small>mdi-plus</v-icon>
                    Agregar primera obra
                  </v-btn>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Diálogo Agregar Obras -->
    <v-dialog
      v-if="showAddObraToLicitacion"
      v-model="showAddObraToLicitacion"
      :width="!$vuetify.breakpoint.mobile ? '80%' : '90%'">
      <AgregarObras
        v-if="showAddObraToLicitacion"
        :licitacionEditar="getLicitacion"
        v-on:cerrar="cerrarVentana">
      </AgregarObras>
    </v-dialog>

    <!-- Diálogo Eliminar Obra -->
    <v-dialog
      v-if="obraAEliminar"
      v-model="showDeleteObraFromLicitacion"
      max-width="500">
      <EliminarObras
        :obraAEliminar="obraAEliminar"
        v-on:cerrar="cerrarVentana">
      </EliminarObras>
    </v-dialog>

    <!-- Diálogo Cancelar Licitación -->
    <v-dialog
      v-model="showCancelarLicitacion"
      max-width="700"
      persistent>
      <v-form
        ref="formCancelarLicitacion"
        v-model="formularioCancelarValido">
        <v-card>
          <v-card-title class="secondary--text">
            Cancelar Licitación
            <v-spacer></v-spacer>
            <v-btn icon @click="cerrarDialogoCancelar">
              <v-icon class="secondary--text" large>highlight_off</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert
              dense
              outlined
              type="warning"
              class="mb-2">
              Esta acción marcará la licitación como cancelada y no se puede revertir desde esta vista.
            </v-alert>
            <custom-text-area
              v-model="cancelacion.motivoCancelacion"
              label="Motivo de cancelación"
              :rules="[reglasCancelacion.requerido, reglasCancelacion.minimo10]"
              auto-grow
              required>
            </custom-text-area>
            <custom-text-area
              v-model="cancelacion.observacionesCancelacion"
              label="Observaciones (opcional)"
              auto-grow>
            </custom-text-area>
          </v-card-text>
          <v-divider class="bkground" />
          <v-card-actions>
            <v-spacer></v-spacer>
            <custom-button
              @click="cerrarDialogoCancelar"
              tipo="formCancelar"
              label="Cancelar"
              class="mr-3">
            </custom-button>
            <custom-button
              @click="confirmarCancelacion"
              tipo="formAceptar"
              label="Confirmar"
              :disabled="!formularioCancelarValido || enviandoCancelacion"
              :loading="enviandoCancelacion">
            </custom-button>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import LICITACION from '@/graphql/querys/administracion/licitaciones/licitacion.gql';
import CANCELAR_LICITACION from '@/graphql/mutations/administracion/licitaciones/cancelarLicitacion.gql';
import AgregarObras from '@/components/administracion/licitaciones/obras/AgregarObras.vue';
import EliminarObras from '@/components/administracion/licitaciones/obras/EliminarObras.vue';
import CustomTextArea from '@/components/inputs/TextArea.vue';
import CustomButton from '@/components/buttons/Button.vue';

export default {
  name: 'Defaultlicitacion',
  components: {
    AgregarObras,
    EliminarObras,
    CustomTextArea,
    CustomButton,
  },
  data() {
    return {
      showAddObraToLicitacion: false,
      showDeleteObraFromLicitacion: false,
      showCancelarLicitacion: false,
      formularioCancelarValido: false,
      enviandoCancelacion: false,
      obraAEliminar: null,
      cancelacion: {
        motivoCancelacion: '',
        observacionesCancelacion: '',
      },
      reglasCancelacion: {
        requerido: (valor) => !!String(valor || '').trim() || 'El motivo es requerido',
        minimo10: (valor) => String(valor || '').trim().length >= 10
          || 'El motivo debe tener al menos 10 caracteres',
      },
      headers: [
        {
          text: 'Clave',
          class: 'grey--text text--darken-3 font-weight-bold',
          align: 'start',
          value: 'obra.clave',
          width: '120',
          sortable: false,
        },
        {
          text: 'Tipo',
          class: 'grey--text text--darken-3 font-weight-bold',
          value: 'obra.tipo',
          width: '150',
          sortable: false,
        },
        {
          text: 'Meta',
          class: 'grey--text text--darken-3 font-weight-bold',
          value: 'obra.meta',
          sortable: false,
        },
        {
          text: 'Acciones',
          class: 'grey--text text--darken-3 font-weight-bold',
          value: 'acciones',
          align: 'end',
          width: '100',
          sortable: false,
        },
      ],
    };
  },
  apollo: {
    licitacion: {
      query: LICITACION,
      variables() {
        return {
          id: this.$route.params.idLicitacion,
        };
      },
    },
  },
  methods: {
    abrirDialogoCancelar() {
      if (!this.puedeCancelarPorRegla) {
        this.mixMensajeError('No se puede cancelar una licitación que ya tiene ganador');
        return;
      }

      this.showCancelarLicitacion = true;
    },
    limpiarFormularioCancelacion() {
      this.cancelacion = {
        motivoCancelacion: '',
        observacionesCancelacion: '',
      };
      this.formularioCancelarValido = false;
      if (this.$refs.formCancelarLicitacion) {
        this.$refs.formCancelarLicitacion.resetValidation();
      }
    },
    cerrarDialogoCancelar() {
      this.showCancelarLicitacion = false;
      this.limpiarFormularioCancelacion();
    },
    async confirmarCancelacion() {
      if (!this.$refs.formCancelarLicitacion.validate()) {
        return;
      }

      if (!this.puedeCancelarPorRegla) {
        this.mixMensajeError('No se puede cancelar una licitación que ya tiene ganador');
        return;
      }

      this.enviandoCancelacion = true;
      this.mixEsCargando();

      try {
        await this.$apollo.mutate({
          mutation: CANCELAR_LICITACION,
          variables: {
            id: this.$route.params.idLicitacion,
            cancelacion: {
              motivoCancelacion: this.cancelacion.motivoCancelacion,
              observacionesCancelacion: this.cancelacion.observacionesCancelacion || null,
            },
          },
        });

        await this.$apollo.queries.licitacion.refetch();
        this.mixMensajeSuccess();
        this.cerrarDialogoCancelar();
      } catch (error) {
        this.mixMensajeError(error);
      } finally {
        this.enviandoCancelacion = false;
        this.mixEsCargando();
      }
    },
    removerObra(obra) {
      this.obraAEliminar = obra;
      this.showDeleteObraFromLicitacion = true;
    },
    formatoFechaHumano(fecha) {
      if (!fecha) return 'Sin fecha';
      return new Date(`${fecha}T00:00:00`).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    cerrarVentana() {
      this.showDeleteObraFromLicitacion = false;
      this.showAddObraToLicitacion = false;
      this.obraAEliminar = null;
    },
  },
  computed: {
    getLicitacion() {
      if (!this.licitacion) return {};
      return this.licitacion;
    },
    estaCancelada() {
      return !!this.getLicitacion.canceladaEn
        || String(this.getLicitacion.estatus || '').toUpperCase() === 'CANCELADA';
    },
    puedeCancelarPorRegla() {
      return !this.getLicitacion.tieneGanador;
    },
    getTipoLicitacion() {
      if (this.licitacion?.tipo === 1) return 'Pública';
      return 'Restringida por Invitación';
    },
    getTotalAnticipo() {
      const material = this.licitacion?.anticipoMaterial || 0;
      const inicio = this.licitacion?.anticipoInicio || 0;
      return material + inicio;
    },
  },
};
</script>

<style lang="scss">
</style>
